import Axios from 'axios';
import Logger from "./Logger";
import NormalizeUtils from "./NormalizeUtils";
import WrapperMessageDispatcher from "./WrapperMessageDispatcher";
import CSRFUtils from './CSRFUtils';
import ErrorHandlingUtils from './ErrorHandlingUtils';
import UserNotificationUtils from './UserNotificationUtils';
import DateTimeUtils from './DateTimeUtils';
import NOOP from './NOOPUtils';
import SentrySDK from './SentrySDK';
const TAG = 'AxiosHttpClient';
const CANCELED = 'canceled';
export default class AxiosHttpClient {
    constructor(sendCrossDomainCookies = false, sendMetrics = true) {
        this.defaultHttpRequestConfig = AxiosHttpClient.buildDefaultHttpRequestConfig();
        this.sendCrossDomainCookies = sendCrossDomainCookies;
        this.sendMetrics = sendMetrics;
    }
    static buildDefaultHttpRequestConfig() {
        const defaultHttpRequestConfig = {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept-Language': 'en-US,en;q=0.9',
            }
        };
        const csrfToken = CSRFUtils.getCSRFToken();
        if (csrfToken !== null) {
            defaultHttpRequestConfig.headers[CSRFUtils.CSRF_TOKEN_HEADER_NAME] = csrfToken;
        }
        return defaultHttpRequestConfig;
    }
    transformConfigToAxiosConfig(config, defaultAxiosConfig) {
        const axiosConfig = this.buildDefaultAxiosConfig(defaultAxiosConfig);
        if (!config) {
            return axiosConfig;
        }
        if (config.headers) {
            axiosConfig.headers = {
                ...axiosConfig.headers,
                ...config.headers
            };
        }
        if (config.signal) {
            axiosConfig.signal = config.signal;
        }
        return axiosConfig;
    }
    buildDefaultAxiosConfig(defaultAxiosConfig) {
        const axiosConfig = defaultAxiosConfig ? defaultAxiosConfig : {};
        if (axiosConfig.headers) {
            axiosConfig.headers = {
                ...this.defaultHttpRequestConfig.headers,
                ...axiosConfig.headers,
            };
        }
        else {
            axiosConfig.headers = {
                ...this.defaultHttpRequestConfig.headers,
            };
        }
        if (this.sendCrossDomainCookies) {
            axiosConfig.withCredentials = this.sendCrossDomainCookies;
        }
        return axiosConfig;
    }
    async get(url, successCallback = NOOP, errorCallback = NOOP, config, axiosConfig) {
        const requestStartMillis = performance.now();
        const requestId = require('uuid').v4();
        Logger.info(TAG, `Sending GET request ${requestId} to '${url}'...`);
        try {
            const response = await Axios.get(url, this.transformConfigToAxiosConfig(config, axiosConfig));
            return this.handleSuccess(url, response, requestStartMillis, requestId, successCallback, errorCallback);
        }
        catch (errorResponse) {
            // cannot just throw the return or else it will break old usages that do not wrap calls in try-catch
            if (typeof errorResponse === 'object' && errorResponse.message === CANCELED) {
                Logger.info(TAG, `Resolving with empty object because GET request ${requestId} to '${url}' got canceled.`);
                return Promise.resolve({});
            }
            else if (Axios.isCancel(errorResponse)) {
                // This separation is particularly useful for the new manual request cancellation flow in the internal user table page,
                // where we want to ignore the error, differentiating it from other error handling logic since this is expected.
                Logger.info(TAG, `Resolving with more descriptive error object because GET request ${requestId} to '${url}' got canceled by the client or within our request logic.`);
                const cancellationError = {
                    id: ErrorHandlingUtils.ERROR_CODE_REQUEST_CANCELLED,
                    message: 'The request was cancelled by the client or within our request logic.'
                };
                return Promise.reject(cancellationError);
            }
            else {
                return Promise.reject(this.handleError(url, errorResponse, requestStartMillis, requestId, errorCallback));
            }
        }
    }
    async getAsBlob(url, successCallback = NOOP, errorCallback = NOOP, config) {
        const requestStartMillis = performance.now();
        const requestId = require('uuid').v4();
        Logger.info(TAG, `Sending GET request ${requestId} to '${url}'...`);
        try {
            const response = await Axios.get(url, this.transformConfigToAxiosConfig(config, { responseType: 'blob' }));
            return this.handleSuccess(url, response, requestStartMillis, requestId, successCallback, errorCallback);
        }
        catch (errorResponse) {
            // cannot just throw the return or else it will break old usages that do not wrap calls in try-catch
            return Promise.reject(this.handleError(url, errorResponse, requestStartMillis, requestId, errorCallback));
        }
    }
    async post(url, data, successCallback = NOOP, errorCallback = NOOP, config) {
        const requestStartMillis = performance.now();
        const requestId = require('uuid').v4();
        const normalizedData = NormalizeUtils.normalize(data);
        Logger.info(TAG, `Sending POST request ${requestId} to '${url}'...`);
        try {
            const response = await Axios.post(url, normalizedData, this.transformConfigToAxiosConfig(config));
            return this.handleSuccess(url, response, requestStartMillis, requestId, successCallback, errorCallback);
        }
        catch (errorResponse) {
            // cannot just throw the return or else it will break old usages that do not wrap calls in try-catch
            return Promise.reject(this.handleError(url, errorResponse, requestStartMillis, requestId, errorCallback));
        }
    }
    async postAsBlob(url, data, successCallback = NOOP, errorCallback = NOOP, config) {
        const requestStartMillis = performance.now();
        const requestId = require('uuid').v4();
        const normalizedData = NormalizeUtils.normalize(data);
        Logger.info(TAG, `Sending POST request ${requestId} to '${url}'...`);
        try {
            const response = await Axios.post(url, normalizedData, this.transformConfigToAxiosConfig(config, { responseType: 'blob' }));
            return this.handleSuccess(url, response, requestStartMillis, requestId, successCallback, errorCallback);
        }
        catch (errorResponse) {
            // cannot just throw the return or else it will break old usages that do not wrap calls in try-catch
            return Promise.reject(this.handleError(url, errorResponse, requestStartMillis, requestId, errorCallback));
        }
    }
    async postFormData(url, data, successCallback = NOOP, errorCallback = NOOP, config) {
        const requestStartMillis = performance.now();
        const requestId = require('uuid').v4();
        Logger.info(TAG, `Sending request ${requestId} to '${url}'...`);
        try {
            const response = await Axios.post(url, data, this.transformConfigToAxiosConfig(config, { headers: { 'Content-Type': 'multipart/form-data' } }));
            return this.handleSuccess(url, response, requestStartMillis, requestId, successCallback, errorCallback);
        }
        catch (errorResponse) {
            // cannot just throw the return or else it will break old usages that do not wrap calls in try-catch
            return Promise.reject(this.handleError(url, errorResponse, requestStartMillis, requestId, errorCallback));
        }
    }
    async put(url, data, successCallback = NOOP, errorCallback = NOOP, config) {
        const requestStartMillis = performance.now();
        const requestId = require('uuid').v4();
        const normalizedData = NormalizeUtils.normalize(data);
        Logger.info(TAG, `Sending PUT request ${requestId} to '${url}'...`);
        try {
            const response = await Axios.put(url, normalizedData, this.transformConfigToAxiosConfig(config));
            return this.handleSuccess(url, response, requestStartMillis, requestId, successCallback, errorCallback);
        }
        catch (errorResponse) {
            // cannot just throw the return or else it will break old usages that do not wrap calls in try-catch
            return Promise.reject(this.handleError(url, errorResponse, requestStartMillis, requestId, errorCallback));
        }
    }
    async delete(url, successCallback = NOOP, errorCallback = NOOP, config) {
        const requestStartMillis = performance.now();
        const requestId = require('uuid').v4();
        Logger.info(TAG, `Sending DELETE request ${requestId} to '${url}'...`);
        try {
            const response = await Axios.delete(url, this.transformConfigToAxiosConfig(config));
            return this.handleSuccess(url, response, requestStartMillis, requestId, successCallback, errorCallback);
        }
        catch (errorResponse) {
            // cannot just throw the return or else it will break old usages that do not wrap calls in try-catch
            return Promise.reject(this.handleError(url, errorResponse, requestStartMillis, requestId, errorCallback));
        }
    }
    handleSuccess(url, response, requestStartMillis, requestId, successCallback, errorCallback) {
        const requestEndMillis = performance.now();
        const requestTimeMillis = requestEndMillis - requestStartMillis;
        if (requestTimeMillis > 5000) {
            Logger.warn(TAG, `Request ${requestId} took longer then 5 seconds: ${requestTimeMillis} ms`);
        }
        if (this.sendMetrics) {
            AxiosHttpClient.sendRequestMetrics(url, requestTimeMillis, response.status);
        }
        if (response.data && typeof response.data === 'object') {
            Logger.debug(TAG, `Request ${requestId} completed successfully in ${requestTimeMillis} ms`);
            AxiosHttpClient.maybePrintDebugInfo(response.data);
            successCallback(response.data);
            return response.data;
        }
        if (response.data &&
            typeof response.data === 'string' &&
            response.headers &&
            typeof response.headers['content-type'] === 'string' &&
            response.headers['content-type'].startsWith('text/')) {
            Logger.debug(TAG, `Request ${requestId} completed successfully in ${requestTimeMillis} ms`);
            AxiosHttpClient.maybePrintDebugInfo(response.data);
            successCallback(response.data);
            return response.data;
        }
        else {
            let errorDetail = 'no data';
            if (typeof response.data === 'string') {
                errorDetail = response.data;
            }
            Logger.error(TAG, `Request ${requestId} got invalid response: ${errorDetail}`);
            // Call error callback
            errorCallback({
                id: 'invalid_response'
            });
            throw errorDetail;
        }
    }
    handleError(url, errorResult, requestStartMillis, requestId, errorCallback) {
        const requestEndMillis = performance.now();
        const requestTimeMillis = requestEndMillis - requestStartMillis;
        const httpCode = (errorResult.response && errorResult.response.status) ? errorResult.response.status : -1;
        const data = errorResult.response && errorResult.response.data ? errorResult.response.data : null;
        Logger.warn(TAG, `Request ${requestId} failed in ${requestTimeMillis} ms with code ${httpCode}`);
        if (this.sendMetrics) {
            AxiosHttpClient.sendRequestMetrics(url, requestTimeMillis, httpCode);
        }
        let errorId = ErrorHandlingUtils.ERROR_CODE_BAD_HTTP;
        let errorParams = {};
        if (data && data.errorMessage && data.errorMessage.id) {
            errorId = data.errorMessage.id;
            errorParams = data.errorMessage.parameters ? data.errorMessage.parameters : null;
            Logger.warn(TAG, `Request ${requestId} error: ${JSON.stringify(data.errorMessage)}`);
        }
        AxiosHttpClient.maybePrintDebugInfo(data);
        errorId = AxiosHttpClient.maybeSetExplicitErrorId(httpCode, errorId, requestId);
        const errorMessageObject = {
            id: errorId,
            parameters: errorParams
        };
        /***** HACK: to set specific error message during nightly downtimes ****/
        // check for utc time
        const utcNow = DateTimeUtils.getUtcNow();
        const startOfIssue = DateTimeUtils.getUtcToday(7, 30);
        const endOfIssue = DateTimeUtils.getUtcToday(10, 0);
        const inIssueTimeFrame = startOfIssue < utcNow && utcNow < endOfIssue;
        let isBadGatewayErrorAndInIssueTimeFrame = false;
        if (inIssueTimeFrame && ErrorHandlingUtils.maybeHandleNightlyIssueError(errorMessageObject)) {
            isBadGatewayErrorAndInIssueTimeFrame = true;
            // disable User Notification
            UserNotificationUtils.disableNotificationsGlobally();
        }
        else {
            ErrorHandlingUtils.maybeHandleCommonHttpError(errorMessageObject);
        }
        errorCallback(errorMessageObject);
        if (isBadGatewayErrorAndInIssueTimeFrame) {
            // setting arbitrary 500ms to delay enabling notifications
            // until after the promise has been rejected and the end usage
            // handler resolves
            setTimeout(() => {
                // enable User Notification
                UserNotificationUtils.enableNotificationsGlobally();
            }, 500);
        }
        /***** END HACK: to set specific error message during nightly downtimes ****/
        SentrySDK.captureMessage(`Request to ${url} failed with status code ${httpCode}`, 'warning', {
            logId: errorParams?.logId || ''
        }, {
            'statusCode': `${httpCode}`,
            'errorId': errorId
        });
        return errorMessageObject;
    }
    static maybeSetExplicitErrorId(httpCode, errorId, requestId) {
        /**
         * Because 413 will be thrown by nginx, the response will not be in the format we want.
         * So we should explicitly set the errorId here.
         */
        if (httpCode === 413) {
            Logger.error(TAG, `Request ${requestId} failed due to large entity size`);
            return ErrorHandlingUtils.ERROR_CODE_LARGE_REQUEST_ENTITY;
        }
        /**
         * Because 502 will be thrown by nginx, the response will not be in the format we want.
         * So we should explicitly set the errorId here.
         */
        if (httpCode === 502) {
            Logger.error(TAG, `Request ${requestId} failed due to large entity size`);
            return ErrorHandlingUtils.ERROR_CODE_BAD_GATEWAY;
        }
        if (httpCode === 504) {
            Logger.error(TAG, `Request ${requestId} failed due to PHP timeout`);
            return ErrorHandlingUtils.ERROR_CODE_TIME_OUT;
        }
        return errorId;
    }
    static maybePrintDebugInfo(data) {
        if (data && data.debugInfo) {
            console.table(data.debugInfo);
            WrapperMessageDispatcher.dispatchMessage('show_debug_info', {
                debugInfo: data.debugInfo
            });
        }
    }
    static sendRequestMetrics(url, latencyMs, httpCode) {
        const metrics = [
            AxiosHttpClient.createRequestMetric(url, 'latency_ms', latencyMs),
            AxiosHttpClient.createRequestMetric(url, httpCode.toString(), 1)
        ];
        Axios.post('/metrics', { metrics });
    }
    static createRequestMetric(url, action, value) {
        const urlWithoutParams = url.split('?')[0];
        const metricName = `${urlWithoutParams.replace(/\//g, '.').substr(1)}.${action}`;
        return {
            key: metricName,
            value
        };
    }
}
//# sourceMappingURL=HttpClient.js.map